import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/SEO';
import { 
  FaCheckCircle, 
  FaLaptopCode, 
  FaShoppingCart, 
  FaBlog, 
  FaPlay, 
  FaArrowRight,
  FaCheck,
  FaDownload,
  FaFileAlt,
  FaChartLine 
} from 'react-icons/fa';
import BrandText from '../../styles/brandStyles';

const ErpWeb = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isSticky, setIsSticky] = useState(false);

  // Función para manejar el scroll y actualizar la sección activa
  useEffect(() => {
    const handleScroll = () => {
      // Detectar si el submenú debe ser sticky
      const heroSection = document.getElementById('overview');
      const headerHeight = 64; // Altura del header principal
      
      if (heroSection) {
        const heroBottom = heroSection.getBoundingClientRect().bottom;
        setIsSticky(heroBottom <= headerHeight);
      } else {
        // Si no se encuentra la sección, mantener el menú sticky para evitar que desaparezca
        setIsSticky(true);
      }

      // Detectar qué sección está visible
      const sections = ['overview', 'features', 'capabilities', 'benefits', 'cta'];
      let activeFound = false;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = 140; // Altura del header + submenu
          if (rect.top <= offset && rect.bottom >= offset) {
            setActiveSection(section);
            activeFound = true;
            break;
          }
        }
      }
      
      // Si no se encuentra ninguna sección activa, mantener la última activa
      if (!activeFound && window.scrollY > 0) {
        // Mantener la sección activa anterior o establecer una por defecto
        setIsSticky(true);
      }
    };

    // Ejecutar handleScroll inmediatamente para establecer el estado inicial
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Función para hacer scroll a una sección
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 140; // Altura del header + submenu
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="OPUS ERP - Sitios Web | ECOSOFT"
        description="Crea y gestiona sitios web profesionales para tu empresa con el módulo Web de OPUS ERP. Incluye comercio electrónico y blog."
        keywords="OPUS, ERP, sitios web, desarrollo web, comercio electrónico, blog, marketing digital"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[450px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0">
          <img 
            src="/images/erpBAN/erp_web.png" 
            alt="OPUS ERP Web" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/1920x450/003171/FFFFFF?text=OPUS+ERP+WEB';
            }}
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <div className="flex items-center mb-6">
              <div className="relative w-auto h-auto mr-6 flex-shrink-0">
                <div className="bg-white/90 rounded-lg px-8 py-3 pt-4 shadow-lg">
                  <img 
                    src="/images/opus-erp-logo.svg" 
                    alt="OPUS ERP" 
                    className="h-12 w-auto object-contain" 
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/128/003171/FFFFFF?text=OPUS+ERP';
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-5xl font-bold"><BrandText>OPUS ERP</BrandText></h1>
                <h2 className="text-4xl font-bold mt-2">Web</h2>
              </div>
            </div>
            <p className="text-xl mb-8">
              Publicita y haz crecer tu empresa. Crea y gestiona sitios web profesionales con herramientas intuitivas.
            </p>
            <div className="flex gap-4">
              <a href="/documents/opus_e_empresas-1.pdf" download className="bg-white text-[#0066CC] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors flex items-center">
                <FaDownload className="mr-2" />
                Folleto Empresas
              </a>
              <a href="/documents/opus_e_constructoras-1.pdf" download className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white/10 transition-colors flex items-center">
                <FaDownload className="mr-2" />
                Folleto Constructoras
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Submenu Navigation */}
      <div 
        className={`bg-white shadow-md transition-all duration-300 sticky-nav ${
          isSticky 
            ? 'fixed top-[64px] left-0 right-0 z-30 border-t border-gray-200' 
            : 'relative'
        }`}
        style={{ minHeight: '56px' }} // Altura mínima para evitar saltos
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex overflow-x-auto py-4 items-center relative">
            <div className="flex-grow flex">
              <button 
                onClick={() => scrollToSection('overview')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'overview' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Resumen
              </button>
              <button 
                onClick={() => scrollToSection('features')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'features' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Características
              </button>
              <button 
                onClick={() => scrollToSection('capabilities')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'capabilities' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Capacidades
              </button>
              <button 
                onClick={() => scrollToSection('benefits')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'benefits' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Beneficios
              </button>
              <button 
                onClick={() => scrollToSection('cta')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'cta' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Comenzar
              </button>
            </div>
            <div className="flex items-center ml-4">
              <div className="bg-white/90 rounded-lg px-4 py-2 shadow-sm">
                <img 
                  src="/images/opus-erp-logo.svg" 
                  alt="OPUS ERP - Web" 
                  className="h-8 w-auto object-contain"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/48/003171/FFFFFF?text=ERP+WEB';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Features Grid */}
      <section id="features" className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Videos */}
            <div className="bg-gray-50 p-6 rounded-lg flex flex-col h-full">
              <div className="w-10 h-10 bg-[#0066CC]/10 rounded-lg flex items-center justify-center mb-4">
                <FaPlay className="w-5 h-5 text-[#0066CC]" />
              </div>
              <h3 className="text-lg font-semibold mb-1">VIDEOS</h3>
              <h4 className="text-base font-medium mb-2">Canal oficial</h4>
              <p className="text-gray-600 mb-4 flex-grow text-sm">
                Consulta el canal oficial y descubre todo lo que OPUS ERP Web tiene para tí.
              </p>
              <Link to="/academia-opus" className="text-[#0066CC] font-medium inline-flex items-center text-sm">
                Ver ahora <FaArrowRight className="ml-2" />
              </Link>
            </div>

            {/* Más Información */}
            <div className="bg-gray-50 p-6 rounded-lg flex flex-col h-full">
              <div className="w-10 h-10 bg-[#0066CC]/10 rounded-lg flex items-center justify-center mb-4">
                <FaFileAlt className="w-5 h-5 text-[#0066CC]" />
              </div>
              <h3 className="text-lg font-semibold mb-1">MÁS INFORMACIÓN</h3>
              <h4 className="text-base font-medium mb-2">Sistema Completo</h4>
              <p className="text-gray-600 mb-4 flex-grow text-sm">
                Conoce todos los módulos y funcionalidades que ofrece OPUS ERP para empresas y constructoras.
              </p>
              <Link to="/productos/opus-erp" className="text-[#0066CC] font-medium inline-flex items-center text-sm">
                Ver sistema completo <FaArrowRight className="ml-2" />
              </Link>
            </div>

            {/* Conoce a Fondo */}
            <div className="bg-gray-50 p-6 rounded-lg flex flex-col h-full">
              <div className="w-10 h-10 bg-[#0066CC]/10 rounded-lg flex items-center justify-center mb-4">
                <FaChartLine className="w-5 h-5 text-[#0066CC]" />
              </div>
              <h3 className="text-lg font-semibold mb-1">CONOCE A FONDO</h3>
              <h4 className="text-base font-medium mb-2">Deseo una demostración</h4>
              <p className="text-gray-600 mb-4 flex-grow text-sm">
                Agenda una presentación personalizada con nuestros expertos.
              </p>
              <Link to="/contacto" className="text-[#0066CC] font-medium inline-flex items-center text-sm">
                Contáctanos <FaArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Arrastra y Suelta Section */}
      <section id="capabilities" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-3">Lo que puedes hacer con OPUS ERP Web</h2>
          <p className="text-xl text-gray-600 text-center mb-10 max-w-3xl mx-auto">
            Descubre cómo OPUS ERP Web te ayuda a crear y gestionar sitios web profesionales para tu empresa, incluyendo comercio electrónico y blog.
          </p>
          
          <div className="space-y-12">
            {/* Constructor Web Capability */}
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="w-full md:w-1/2">
                <img 
                  src="/images/modules/opus_erp/001 compras.png" 
                  alt="Constructor de sitios web OPUS" 
                  className="rounded-lg shadow-lg w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600x400/003171/FFFFFF?text=CONSTRUCTOR+WEB+OPUS';
                  }}
                />
              </div>
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold mb-3">Arrastra y suelta</h3>
                <p className="text-gray-600 mb-3">
                  Gestiona tus compras con facilidad usando nuestra interfaz de arrastrar y soltar, que simplifica el proceso de adquisición y seguimiento de órdenes de compra.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Monta un sitio web y añade funciones a medida que tu negocio crece.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Creación intuitiva sin conocimientos técnicos.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Plantillas profesionales adaptables a tu marca.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Diseño totalmente responsive para dispositivos móviles.</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Ecommerce Capability */}
            <div className="flex flex-col md:flex-row-reverse gap-6 items-center">
              <div className="w-full md:w-1/2">
                <img 
                  src="/images/modules/opus_erp/002 proyectos.png" 
                  alt="Comercio Electrónico OPUS" 
                  className="rounded-lg shadow-lg w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600x400/003171/FFFFFF?text=ECOMMERCE+OPUS';
                  }}
                />
              </div>
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold mb-3">Comercio electrónico</h3>
                <p className="text-gray-600 mb-3">
                  Administra proyectos completos de comercio electrónico desde su planificación hasta su implementación, incluyendo seguimiento de costos, recursos y plazos.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Tienda en línea: increíbles páginas de productos.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Totalmente accesible a través de dispositivos móviles.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Gestión completa de inventario, pedidos y pagos.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Integración con múltiples pasarelas de pago.</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Blog Capability */}
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="w-full md:w-1/2">
                <img 
                  src="/images/modules/opus_erp/003 contabilidad.png" 
                  alt="Blog OPUS" 
                  className="rounded-lg shadow-lg w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600x400/003171/FFFFFF?text=BLOG+OPUS';
                  }}
                />
              </div>
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-bold mb-3">Blog</h3>
                <p className="text-gray-600 mb-3">
                  Integra sistema contable completo en tu blog empresarial para monitorear ingresos por publicidad, costos operativos y rendimiento financiero del contenido.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Desarrolla tu estrategia de marketing de contenido.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Involucra y retén tu audiencia en todas las plataformas.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Editor intuitivo con múltiples formatos de contenido.</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                    <span>Optimización SEO integrada para mejor visibilidad.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Key Benefits */}
      <section id="benefits" className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Beneficios clave</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center mt-1 mr-4">
                  <FaCheck className="text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">Mayor presencia online</h3>
                  <p className="text-gray-600">
                    Establece una presencia profesional en internet y aumenta tu visibilidad.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center mt-1 mr-4">
                  <FaCheck className="text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">Ventas online 24/7</h3>
                  <p className="text-gray-600">
                    Vende tus productos o servicios a cualquier hora y desde cualquier lugar.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center mt-1 mr-4">
                  <FaCheck className="text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">Creación sin código</h3>
                  <p className="text-gray-600">
                    Desarrolla sitios web profesionales sin necesidad de conocimientos técnicos.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center mt-1 mr-4">
                  <FaCheck className="text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">Marketing de contenidos</h3>
                  <p className="text-gray-600">
                    Atrae y retén a tu audiencia con contenido relevante y de calidad.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center mt-1 mr-4">
                  <FaCheck className="text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">Análisis de rendimiento</h3>
                  <p className="text-gray-600">
                    Monitorea y optimiza el tráfico, conversiones y comportamiento de usuarios.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center mt-1 mr-4">
                  <FaCheck className="text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">Integración completa</h3>
                  <p className="text-gray-600">
                    Se integra con otros módulos OPUS ERP para un flujo de trabajo unificado.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Integration with other modules */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#1A2B39]">Automatiza todos los procesos de tu empresa con OPUS ERP</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Descubre los diferentes módulos de OPUS ERP y cómo pueden ayudarte a optimizar tu negocio
          </p>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <Link to="/erp/operaciones" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Operaciones</h3>
              <p className="text-gray-600 text-sm">Haz más en menos tiempo.</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <Link to="/erp/ventas" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Ventas</h3>
              <p className="text-gray-600 text-sm">De la propuesta a la firma electrónica.</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <Link to="/erp/productividad" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Productividad</h3>
              <p className="text-gray-600 text-sm">Los hechos tan rápido como el pensamiento.</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <div className="bg-[#F5F7FA] p-5 rounded-lg text-center border-2 border-[#0066CC] hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Web</h3>
              <p className="text-gray-600 text-sm">Creación de webs para tu empresa.</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section id="cta" className="py-16 bg-[#1A2B39] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">¿Listo para potenciar tu presencia digital?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Descubre todo el potencial de OPUS ERP Web para crear y gestionar una presencia online
            profesional para tu empresa de construcción.
          </p>
          <div className="flex flex-wrap justify-center">
            <Link 
              to="/Distribuidores"
              className="px-10 py-4 bg-[#0066CC] hover:bg-[#0088FF] text-white text-lg font-medium rounded-md transition-colors"
            >
              Solicitar Demostración
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ErpWeb; 